import { externalConstraint } from '@flatfile/plugin-constraints'
import { recordHook } from '@flatfile/plugin-record-hook'
import { iraAmountValidator, recordkeeperListener } from './listeners'
import {
  formatPlanEin,
  formatPlanSponsor,
  formatPlanName,
  formatPlanIdNumber,
  formatPlanNumber,
  formatZipCode,
  formatState,
  validateEmailAddress,
  formatRecordkeeper,
  formatNotificationsCountryCode,
  formatBalanceVested,
  formatBalanceTotal,
  formatDateOfBalance,
  validateDateOfBalance,
  formatReasonForRolloutRequest,
  validateReasonForRolloutRequest,
  formatGracePeriod,
  formatCompanyState,
  formatCompanyZipCode,
  validateZipCode,
  validateCompanyZipCode,
  validateCompanyEmail,
} from './participantsDataFormatting'
import { buildNotificationsValidationContext } from './notificationsValidationContext'
import { notificationServicesFields } from '../config/notificationsFields'

export const notificationsParticipantListeners = (listener, userAudience) => {
  const ctx = buildNotificationsValidationContext(userAudience)

  // Formatting: single hook runs all field formatters in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      formatRecordkeeper(record)
      formatPlanEin(record)
      formatPlanSponsor(record)
      formatPlanName(record)
      formatPlanIdNumber(record)
      formatPlanNumber(record)
      formatZipCode(record)
      formatNotificationsCountryCode(record)
      formatState(record)
      formatBalanceVested(record)
      formatBalanceTotal(record)
      formatDateOfBalance(record)
      formatReasonForRolloutRequest(record)
      formatGracePeriod(record)
      formatCompanyState(record)
      formatCompanyZipCode(record)
      return record
    })
  )

  // Validation: single hook runs all validators in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      validateEmailAddress(record)
      validateDateOfBalance(record)
      validateReasonForRolloutRequest(record)
      validateZipCode(record)
      validateCompanyZipCode(record)
      validateCompanyEmail(record)
      return record
    })
  )

  listener.use(
    externalConstraint('recordKeeper', (value, key, { record }) => {
      const rules = ctx.recordkeeper
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
    })
  )

  recordkeeperListener(listener, 'participant-record')

  listener.use(
    externalConstraint('tpa', (value, key, { record }) => {
      const rules = ctx.tpa
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (ctx.tpaRequired && value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planSponsor', (value, key, { record }) => {
      const rules = ctx.planSponsor
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.invalidString.re.test(value))
        record.addError(key, rules.invalidString.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planName', (value, key, { record }) => {
      const rules = ctx.planName
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planEin', (value, key, { record }) => {
      const rules = ctx.planEin
      if (value && !rules.invalidEINFormat.re.test(value))
        record.addError(key, rules.invalidEINFormat.error)
      if (value && !rules.invalidEIN.re.test(value))
        record.addError(key, rules.invalidEIN.error)
    })
  )

  listener.use(
    externalConstraint('planNumber', (value, key, { record }) => {
      const rules = ctx.planNumber
      if (value && rules.invalidChars.re.test(value))
        record.addError(key, rules.invalidChars.error)
      if (value && !rules.mustBeThreeDigits.re.test(value))
        record.addError(key, rules.mustBeThreeDigits.error)
    })
  )

  listener.use(
    externalConstraint('planIdNumber', (value, key, { record }) => {
      const { maxChar } = ctx.planIdNumber
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('firstName', (value, key, { record }) => {
      const rules = ctx.firstName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.namePrefix.re.test(value))
        record.addError(key, rules.namePrefix.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('middleName', (value, key, { record }) => {
      const rules = ctx.middleName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('lastName', (value, key, { record }) => {
      const rules = ctx.lastName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('addressLine1', (value, key, { record }) => {
      const rules = ctx.addressLine1
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && !rules.mustContainNumberAndLetter.re.test(value))
        record.addError(key, rules.mustContainNumberAndLetter.error)
    })
  )

  listener.use(
    externalConstraint('addressLine2', (value, key, { record }) => {
      const { maxChar } = ctx.addressLine2
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('city', (value, key, { record }) => {
      const rules = ctx.city
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('state', (value, key, { record }) => {
      const rules = ctx.state
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('zipCode', (value, key, { record }) => {
      const rules = ctx.zipCode
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('countryCode', (value, key, { record }) => {
      const { emptyValue } = ctx.countryCode
      if (value && emptyValue.re.test(value))
        record.addError(key, emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('emailAddress', (value, key, { record }) => {
      const { maxChar } = ctx.emailAddress
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.BAL_VESTED.key,
      (value, key, { record }) => {
        const { maxChar } = ctx.vestedBalance
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.BAL_TOTAL.key,
      (value, key, { record }) => {
        const { maxChar } = ctx.totalBalance
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.REASON_ROLLOUT_REQ.key,
      (value, key, { record }) => {
        const { reasonRolloutReq } = ctx.reason
        if (value && !reasonRolloutReq.re.test(value))
          record.addError(key, reasonRolloutReq.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
      (value, key, { record }) => {
        const { gracePeriodToRespond } = ctx.gracePeriod
        if (value && !gracePeriodToRespond.re.test(value))
          record.addError(key, gracePeriodToRespond.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_NAME.key,
      (value, key, { record }) => {
        const rules = ctx.companyName
        if (value && rules.noSlash.re.test(value))
          record.addError(key, rules.noSlash.error)
        if (value && rules.maxChar.re.test(value))
          record.addError(key, rules.maxChar.error)
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_ADDRESS_1.key,
      (value, key, { record }) => {
        const rules = ctx.companyAddressLine1
        if (value && rules.maxChar.re.test(value))
          record.addError(key, rules.maxChar.error)
        if (value && rules.notApplicable.re.test(value))
          record.addError(key, rules.notApplicable.error)
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_ADDRESS_2.key,
      (value, key, { record }) => {
        const { maxChar } = ctx.companyAddressLine2
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_CITY.key,
      (value, key, { record }) => {
        const rules = ctx.companyCity
        if (value && rules.maxChar.re.test(value))
          record.addError(key, rules.maxChar.error)
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_STATE.key,
      (value, key, { record }) => {
        const rules = ctx.companyState
        if (value && rules.maxChar.re.test(value))
          record.addError(key, rules.maxChar.error)
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_ZIP.key,
      (value, key, { record }) => {
        const rules = ctx.companyZipCode
        if (value && rules.maxChar.re.test(value))
          record.addError(key, rules.maxChar.error)
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_CONTACT_PHONE.key,
      (value, key, { record }) => {
        const rules = ctx.companyPhone
        if (value && rules.emptyValue.re.test(value))
          record.addError(key, rules.emptyValue.error)
        if (value && !rules.phoneRegex.re.test(value))
          record.addError(key, rules.phoneRegex.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_WEBSITE.key,
      (value, key, { record }) => {
        const { maxChar } = ctx.companyWebsite
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  listener.use(
    externalConstraint(
      notificationServicesFields.COMPANY_CONTACT_EMAIL.key,
      (value, key, { record }) => {
        const { maxChar } = ctx.companyEmail
        if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
      }
    )
  )

  iraAmountValidator(
    listener,
    'participant-record',
    [notificationServicesFields.BAL_VESTED.key],
    notificationServicesFields.BAL_TOTAL.key,
    'Balance (vested) or Balance (total) is required'
  )

  iraAmountValidator(
    listener,
    'participant-record',
    [notificationServicesFields.BAL_TOTAL.key],
    notificationServicesFields.BAL_VESTED.key,
    'Balance (vested) or Balance (total) is required'
  )
}
